<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { getInboxIconByType } from 'dashboard/helper/inbox';
import { CAMPAIGN_TYPES } from 'shared/constants/campaign';

import CardLayout from 'dashboard/components-next/CardLayout.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import LiveChatCampaignDetails from './LiveChatCampaignDetails.vue';
import SMSCampaignDetails from './SMSCampaignDetails.vue';

const props = defineProps({
  title: {
    type: String,
    default: '',
  },
  message: {
    type: String,
    default: '',
  },
  isLiveChatType: {
    type: Boolean,
    default: false,
  },
  isEnabled: {
    type: Boolean,
    default: false,
  },
  status: {
    type: String,
    default: '',
  },
  sender: {
    type: Object,
    default: null,
  },
  inbox: {
    type: Object,
    default: null,
  },
  scheduledAt: {
    type: Number,
    default: 0,
  },
  campaignType: {
    type: String,
    default: '',
  },
  reportSummary: {
    type: Object,
    default: null,
  },
  isClickable: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['edit', 'delete', 'click']);

const { t } = useI18n();

const STATUS_COMPLETED = 'completed';

const { formatMessage } = useMessageFormatter();

const isActive = computed(() =>
  props.isLiveChatType ? props.isEnabled : props.status !== STATUS_COMPLETED
);

// A one_off campaign that already ran cannot be deleted, the backend blocks it
// to preserve the dispatch report.
const isDispatched = computed(
  () =>
    props.campaignType === CAMPAIGN_TYPES.ONE_OFF &&
    props.status === STATUS_COMPLETED
);

const reportSummaryLabel = computed(() => {
  const summary = props.reportSummary;
  if (!summary) return '';

  const sent =
    (summary.sent ?? 0) + (summary.delivered ?? 0) + (summary.read ?? 0);
  const failed = summary.failed ?? 0;
  const skipped = summary.skipped ?? 0;

  // Nothing dispatched yet (eg. a scheduled campaign that hasn't fired): skip the
  // "0 enviados · 0 falhas" noise until there's an actual outcome to report.
  if (sent + failed + skipped === 0) return '';

  return skipped > 0
    ? t('CAMPAIGN.WHATSAPP.REPORT.CARD_SUMMARY_WITH_SKIPPED', {
        sent,
        failed,
        skipped,
      })
    : t('CAMPAIGN.WHATSAPP.REPORT.CARD_SUMMARY', { sent, failed });
});

// Only a dispatched campaign has a report to open — a scheduled/never-run one would
// open an empty modal indistinguishable from a legacy (pre-report) dispatch.
const canOpenReport = computed(() => props.isClickable && isDispatched.value);

const handleCardClick = () => {
  if (canOpenReport.value) emit('click');
};

const statusTextColor = computed(() => ({
  'text-n-teal-11': isActive.value,
  'text-n-slate-12': !isActive.value,
}));

const campaignStatus = computed(() => {
  if (props.isLiveChatType) {
    return props.isEnabled
      ? t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.ENABLED')
      : t('CAMPAIGN.LIVE_CHAT.CARD.STATUS.DISABLED');
  }

  return props.status === STATUS_COMPLETED
    ? t('CAMPAIGN.SMS.CARD.STATUS.COMPLETED')
    : t('CAMPAIGN.SMS.CARD.STATUS.SCHEDULED');
});

const inboxName = computed(() => props.inbox?.name || '');

const inboxIcon = computed(() => {
  const { medium, channel_type: type } = props.inbox;
  return getInboxIconByType(type, medium);
});
</script>

<template>
  <CardLayout
    layout="row"
    :class="canOpenReport ? 'cursor-pointer' : ''"
    @click="handleCardClick"
  >
    <div class="flex flex-col items-start justify-between flex-1 min-w-0 gap-2">
      <div class="flex items-center justify-between gap-3 w-fit">
        <span
          class="text-base font-medium capitalize text-n-slate-12 line-clamp-1"
        >
          {{ title }}
        </span>
        <span
          class="text-xs font-medium inline-flex items-center h-6 px-2 py-0.5 rounded-md bg-n-alpha-2"
          :class="statusTextColor"
        >
          {{ campaignStatus }}
        </span>
        <span
          v-if="reportSummaryLabel"
          class="text-xs text-n-slate-11 whitespace-nowrap"
        >
          {{ reportSummaryLabel }}
        </span>
      </div>
      <div
        v-dompurify-html="formatMessage(message, false, false, false)"
        class="text-sm text-n-slate-11 line-clamp-1 [&>p]:mb-0 h-6"
      />
      <div class="flex items-center w-full h-6 gap-2 overflow-hidden">
        <LiveChatCampaignDetails
          v-if="isLiveChatType"
          :sender="sender"
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
        />
        <SMSCampaignDetails
          v-else
          :inbox-name="inboxName"
          :inbox-icon="inboxIcon"
          :scheduled-at="scheduledAt"
        />
      </div>
    </div>
    <div class="flex items-center justify-end w-20 gap-2">
      <Button
        v-if="isLiveChatType"
        variant="faded"
        size="sm"
        color="slate"
        icon="i-lucide-sliders-vertical"
        @click.stop="emit('edit')"
      />
      <Button
        v-if="!isDispatched"
        variant="faded"
        color="ruby"
        size="sm"
        icon="i-lucide-trash"
        @click.stop="emit('delete')"
      />
    </div>
  </CardLayout>
</template>
